import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { isConfigurationEnum, isNullOrUndefined, isNumber, isObject } from '@/util/typeGuards';
import { validateDetail } from './blocks';

export const enumUtil = (): ValidationObject<{
  value: unknown;
  index: number | undefined;
  allData: Record<string, unknown>;
}> & {
  validateBasic: (blockName: string, configuration: Configuration, details: Detail) => ValidationError[];
} => {
  const validateBasic = (blockName: string, configuration: Configuration, details: Detail): ValidationError[] => {
    const errors: ValidationError[] = [];
    if (isNullOrUndefined(details[configuration.value]) && configuration.required) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing a required enum value: ${configuration.label}`,
        id: parseInt(blockName)
      });
    }
    return errors;
  };

  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    if (isConfigurationEnum(configuration)) {
      const basicErrors = validateBasic(blockName, configuration, details);
      if (basicErrors.length) {
        errors.push(...basicErrors);
      } else {
        const values = getValues(configuration, details);

        if (!isNullOrUndefined(details[configuration.value])) {
          if (isNumber(values.index) && configuration.sections) {
            const sectionErrors = configuration.sections[values.index]
              .map((conf) => {
                const initialValuePart =
                  conf.type === 'enum' ? values.allData : values.allData && values.allData[conf.value];
                if (conf.required && initialValuePart === undefined) {
                  return {
                    severity: 'error',
                    message: `Block ${blockName} is missing a required enum value: ${conf.label}`,
                    id: nodeId
                  } as ValidationError;
                } else {
                  return validateDetail(
                    application,
                    metadata,
                    linkedSubroutineId,
                    nodeId,
                    blockName,
                    conf,
                    initialValuePart as Detail
                  ).flat();
                }
              })
              .flat();
            if (sectionErrors.length) {
              errors.push(...sectionErrors);
            }
          } else if (!isNumber(values.index) || configuration.options[values.index] === undefined) {
            errors.push({
              severity: 'error',
              message: `Block ${blockName} refers to a section that doesn't exist.`,
              id: nodeId
            });
          }
        }
      }
    } else {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} contains an invalid enum type`,
        id: nodeId
      });
    }
    return errors;
  };

  const getValues = (
    configuration: Configuration,
    initialValue: unknown
  ): {
    value: unknown;
    index: number | undefined;
    allData: Record<string, unknown>;
  } => {
    const initVal = initialValue as any;
    const optionIndex = isConfigurationEnum(configuration)
      ? configuration.options?.findIndex((option) => option.value === initVal[configuration.value])
      : undefined;
    if (isObject(initVal)) {
      const enumValue = initVal[configuration.value];
      return {
        value: enumValue,
        index: optionIndex !== -1 ? optionIndex : undefined,
        allData: initVal
      };
    }
    return { allData: {}, index: undefined, value: undefined };
  };

  return {
    validateBasic: validateBasic,
    validate: validate,
    getValue: getValues
  };
};
